import { FontEntry, FontFaceSource, FontFile, FontStyle, FontWeightRange } from '../../types/font'

import { austinNewsDeck } from './austin-news-deck'
import { cabin } from './cabin'
import { cinzel } from './cinzel'
import { classicGrotesquePro } from './classic-grotesque-pro'
import { everett } from './everett'
import { getFontFallbackFaceRules, getFontFallbackFamilies } from './fallbacks'
import { neueHaasGroteskText } from './neue-haas-grotesk-text'
import { poppins } from './poppins'
import { raleway } from './raleway'
import { roboto } from './roboto'
import { robotoFlexTtt } from './roboto-flex-ttt'
import { sailec } from './sailec'
import { tomorrow } from './tomorrow'

export const FONT_FILES: Record<string, FontEntry> = {
  'austin-news-deck': austinNewsDeck,
  cabin,
  cinzel,
  'classic-grotesque-pro': classicGrotesquePro,
  poppins,
  everett,
  'neue-haas-grotesk-text': neueHaasGroteskText,
  raleway,
  roboto,
  'roboto-flex-ttt': robotoFlexTtt,
  sailec,
  tomorrow
}

const getFontFileUrl = (fontFile: FontFile) =>
  `https://cdn.courseticket.com/s/fonts/${fontFile.file}.${fontFile.extension}`

const DEFAULT_VARIABLE_WEIGHT_RANGE: FontWeightRange = [100, 900]
const FONT_STYLES: FontStyle[] = ['normal', 'italic']

const toFontFaceSource = (
  family: string,
  style: FontStyle,
  weight: FontWeightRange,
  fontFile: FontFile
): FontFaceSource => ({
  family,
  style,
  weight,
  url: getFontFileUrl(fontFile),
  format: fontFile.format
})

export const getFontFaceSources = (formattedName: string): FontFaceSource[] => {
  const entry = FONT_FILES[formattedName]
  if (!entry) return []

  const { displayName, variables, weights } = entry

  if (variables) {
    return FONT_STYLES.flatMap((style) => {
      const font = variables[style]
      if (!font) return []
      const weight = font.weightRange ?? DEFAULT_VARIABLE_WEIGHT_RANGE
      return [toFontFaceSource(displayName, style, weight, font)]
    })
  }

  return Object.entries(weights).flatMap(([weightStr, styles]) => {
    const weight = Number(weightStr)
    return FONT_STYLES.flatMap((style) => {
      const fontFile = styles[style]
      return fontFile ? [toFontFaceSource(displayName, style, [weight, weight], fontFile)] : []
    })
  })
}

export const formatFontWeight = ([min, max]: FontWeightRange) =>
  min === max ? String(min) : `${min} ${max}`

export const getFontFaceRule = (source: FontFaceSource) => `
@font-face {
  font-family: '${source.family}';
  font-style: ${source.style};
  font-weight: ${formatFontWeight(source.weight)};
  font-display: swap;
  src: url('${source.url}') format('${source.format}');
}`

export const getFontFaceStyles = (formattedName: string) =>
  getFontFaceSources(formattedName).map(getFontFaceRule).join('\n')

const NORMAL_WEIGHT_RANGE_END = 500
const LIGHT_WEIGHT_THRESHOLD = 400

const getWeightMatchRank = ([min, max]: FontWeightRange, weight: number): number[] => {
  if (min <= weight && weight <= max) return [0, 0]

  const isLighter = max < weight
  const lighterRank = [isLighter ? 0 : 1, isLighter ? weight - max : min - weight]
  const heavierRank = [isLighter ? 1 : 0, isLighter ? weight - max : min - weight]

  if (weight < LIGHT_WEIGHT_THRESHOLD) return [1, ...lighterRank]
  if (weight > NORMAL_WEIGHT_RANGE_END) return [1, ...heavierRank]

  const isWithinNormalRange = !isLighter && min <= NORMAL_WEIGHT_RANGE_END
  if (isWithinNormalRange) return [1, 0, min - weight]
  return [2, ...lighterRank]
}

const compareRanks = (a: number[], b: number[]) => {
  const index = a.findIndex((value, i) => value !== b[i])
  return index === -1 ? 0 : a[index] - b[index]
}

const pickClosestWeight = (sources: FontFaceSource[], weight: number) =>
  [...sources].sort((a, b) =>
    compareRanks(getWeightMatchRank(a.weight, weight), getWeightMatchRank(b.weight, weight))
  )[0]

export const findFontFace = (
  sources: FontFaceSource[],
  family: string,
  weight: number,
  style: FontStyle
): FontFaceSource | undefined => {
  const familySources = sources.filter(
    (source) => source.family.toLowerCase() === family.toLowerCase()
  )
  const styleSources = familySources.filter((source) => source.style === style)
  const candidates = styleSources.length ? styleSources : familySources
  return candidates.length ? pickClosestWeight(candidates, weight) : undefined
}

export const getAvailableFonts = (): string[] => {
  return Object.keys(FONT_FILES)
    .map((fileName) => fileName.replaceAll('-', ' '))
    .sort()
}

const GENERIC_FONT_FAMILY = 'sans-serif'

export const getFontStack = (formattedName: string) =>
  [
    formattedName,
    ...getFontFallbackFamilies(formattedName, FONT_FILES[formattedName]?.fallbackMetrics),
    GENERIC_FONT_FAMILY
  ].join(', ')

export const getFontFallbackStyles = (formattedName: string) =>
  getFontFallbackFaceRules(formattedName, FONT_FILES[formattedName]?.fallbackMetrics)
